import ApiClient from '../ApiClient';
import { NiceResponse } from '../dto/NiceResponse';
import { CreateReviewRequest } from './dto/CreateReviewRequest.dto';
import { UpdateReviewRequest } from './dto/UpdateReviewRequest.dto';
import { BulkUpdateReviewsRequest } from './dto/BulkUpdateReviewsRequest.dto';
import { ReviewListParams } from './dto/ReviewListParams.dto';
import { ReviewResponse, ReviewListResponse } from './dto/ReviewResponse.dto';
import { GetAuthorsRequest, GetTagsRequest } from './dto/EntitiesRequest.dto';
import {
  AuthorsListResponse,
  TagsListResponse,
} from './dto/EntitiesResponse.dto';
import {
  CreateReviewTagRequest,
  UpdateReviewTagRequest,
  ReviewTagListParams,
} from './dto/ReviewTagsRequest.dto';
import {
  ReviewTagResponse,
  ReviewTagListResponse,
} from './dto/ReviewTagsResponse.dto';
import {
  CreateCountryRequest,
  UpdateCountryRequest,
  CountryListParams,
  CreateCityRequest,
  UpdateCityRequest,
  CityListParams,
} from './dto/LocationsRequest.dto';
import {
  CountryResponse,
  CountryListResponse,
  CityResponse,
  CityListResponse,
  LocationActionResponse,
} from './dto/LocationsResponse.dto';
import {
  CreateCourseRequest,
  UpdateCourseRequest,
  CourseListParams,
  CreatePersonRequest,
  UpdatePersonRequest,
  PersonListParams,
} from './dto/CoursesAndPersonsRequest.dto';
import {
  CourseResponse,
  CourseListResponse,
  PersonResponse,
  PersonListResponse,
  CoursePersonActionResponse,
} from './dto/CoursesAndPersonsResponse.dto';

export class ReviewsApi {
  // eslint-disable-next-line no-unused-vars
  constructor(private api: ApiClient) {}

  path = '/admin/review/reviews';

  /**
   * Создание нового отзыва
   */
  async create(
    req: CreateReviewRequest,
  ): Promise<NiceResponse<ReviewResponse>> {
    const response = await this.api.client.post<NiceResponse<ReviewResponse>>(
      this.path,
      req,
    );
    return response.data;
  }

  /**
   * Получение отзыва по ID
   */
  async getById(id: string): Promise<NiceResponse<ReviewResponse>> {
    const response = await this.api.client.get<NiceResponse<ReviewResponse>>(
      `${this.path}/${id}`,
    );
    return response.data;
  }

  /**
   * Обновление отзыва
   */
  async update(
    id: string,
    req: UpdateReviewRequest,
  ): Promise<NiceResponse<ReviewResponse>> {
    const response = await this.api.client.put<NiceResponse<ReviewResponse>>(
      `${this.path}/${id}`,
      req,
    );
    return response.data;
  }

  /**
   * Удаление отзыва
   */
  async delete(id: string): Promise<NiceResponse<null>> {
    const response = await this.api.client.delete<NiceResponse<null>>(
      `${this.path}/${id}`,
    );
    return response.data;
  }

  /**
   * Получение списка отзывов с фильтрацией и пагинацией
   */
  async getList(
    params?: ReviewListParams,
  ): Promise<NiceResponse<ReviewListResponse>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }

    const queryString = queryParams.toString();
    const url = queryString ? `${this.path}?${queryString}` : this.path;

    const response = await this.api.client.get<
      NiceResponse<ReviewListResponse>
    >(url);
    return response.data;
  }

  /**
   * Массовое обновление отзывов
   */
  async bulkUpdate(req: BulkUpdateReviewsRequest): Promise<NiceResponse<null>> {
    const response = await this.api.client.post<NiceResponse<null>>(
      `${this.path}/bulk-update`,
      req,
    );
    return response.data;
  }

  // === Методы для получения связанных сущностей ===

  /**
   * Получение списка авторов
   */
  async getAuthors(
    params?: GetAuthorsRequest,
  ): Promise<NiceResponse<AuthorsListResponse>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `/admin/authors?${queryString}`
      : '/admin/authors';

    const response = await this.api.client.get<
      NiceResponse<AuthorsListResponse>
    >(url);
    return response.data;
  }

  /**
   * Получение списка тегов
   */
  async getTags(
    params?: GetTagsRequest,
  ): Promise<NiceResponse<TagsListResponse>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/admin/tags?${queryString}` : '/admin/tags';

    const response = await this.api.client.get<NiceResponse<TagsListResponse>>(
      url,
    );
    return response.data;
  }

  // === Методы для работы с тегами отзывов ===

  /**
   * Создание тега с переводами
   */
  async createReviewTag(
    req: CreateReviewTagRequest,
  ): Promise<NiceResponse<ReviewTagResponse>> {
    const response = await this.api.client.post<
      NiceResponse<ReviewTagResponse>
    >('/admin/review/tags/full', req);
    return response.data;
  }

  /**
   * Получение тега по ID
   */
  async getReviewTagById(id: string): Promise<NiceResponse<ReviewTagResponse>> {
    const response = await this.api.client.get<NiceResponse<ReviewTagResponse>>(
      `/admin/review/tags/${id}`,
    );
    return response.data;
  }

  /**
   * Получение списка тегов с фильтрацией
   */
  async getReviewTags(
    params?: ReviewTagListParams,
  ): Promise<NiceResponse<ReviewTagListResponse>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `/admin/review/tags?${queryString}`
      : '/admin/review/tags';

    const response = await this.api.client.get<
      NiceResponse<ReviewTagListResponse>
    >(url);
    return response.data;
  }

  /**
   * Обновление тега
   */
  async updateReviewTag(
    id: string,
    req: UpdateReviewTagRequest,
  ): Promise<NiceResponse<ReviewTagResponse>> {
    const response = await this.api.client.put<NiceResponse<ReviewTagResponse>>(
      `/admin/review/tags/${id}`,
      req,
    );
    return response.data;
  }

  /**
   * Удаление тега
   */
  async deleteReviewTag(id: string): Promise<NiceResponse<null>> {
    const response = await this.api.client.delete<NiceResponse<null>>(
      `/admin/review/tags/${id}`,
    );
    return response.data;
  }

  /**
   * Поиск тегов по тексту
   */
  async searchReviewTags(
    searchText: string,
    locale?: 'ru' | 'en',
    limit: number = 20,
  ): Promise<NiceResponse<ReviewTagListResponse>> {
    return this.getReviewTags({
      search: searchText,
      locale,
      limit,
      page: 1,
    });
  }

  /**
   * Получение тегов для автокомплита
   */
  async getReviewTagsForAutocomplete(
    query?: string,
    locale?: 'ru' | 'en',
  ): Promise<NiceResponse<ReviewTagListResponse>> {
    return this.getReviewTags({
      search: query,
      locale,
      limit: 50,
      page: 1,
      sort_by: 'created_at',
      sort_dir: 'asc',
    });
  }

  // === Методы для работы со странами ===

  /**
   * Создание страны с переводами
   */
  async createCountry(
    req: CreateCountryRequest,
  ): Promise<NiceResponse<CountryResponse>> {
    const response = await this.api.client.post<NiceResponse<CountryResponse>>(
      '/admin/review/countries/full',
      req,
    );
    return response.data;
  }

  /**
   * Получение страны по ID
   */
  async getCountryById(id: string): Promise<NiceResponse<CountryResponse>> {
    const response = await this.api.client.get<NiceResponse<CountryResponse>>(
      `/admin/review/countries/${id}`,
    );
    return response.data;
  }

  /**
   * Получение списка стран с фильтрацией
   */
  async getCountries(
    params?: CountryListParams,
  ): Promise<NiceResponse<CountryListResponse>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `/admin/review/countries?${queryString}`
      : '/admin/review/countries';

    const response = await this.api.client.get<
      NiceResponse<CountryListResponse>
    >(url);
    return response.data;
  }

  /**
   * Обновление страны
   */
  async updateCountry(
    id: string,
    req: UpdateCountryRequest,
  ): Promise<NiceResponse<CountryResponse>> {
    const response = await this.api.client.put<NiceResponse<CountryResponse>>(
      `/admin/review/countries/${id}`,
      req,
    );
    return response.data;
  }

  /**
   * Удаление страны
   */
  async deleteCountry(
    id: string,
  ): Promise<NiceResponse<LocationActionResponse>> {
    const response = await this.api.client.delete<
      NiceResponse<LocationActionResponse>
    >(`/admin/review/countries/${id}`);
    return response.data;
  }

  /**
   * Поиск стран
   */
  async searchCountries(
    searchText: string,
    limit: number = 20,
  ): Promise<NiceResponse<CountryListResponse>> {
    return this.getCountries({
      search: searchText,
      limit,
      page: 1,
    });
  }

  // === Методы для работы с городами ===

  /**
   * Создание города с переводами
   */
  async createCity(
    req: CreateCityRequest,
  ): Promise<NiceResponse<CityResponse>> {
    const response = await this.api.client.post<NiceResponse<CityResponse>>(
      '/admin/review/cities/full',
      req,
    );
    return response.data;
  }

  /**
   * Получение города по ID
   */
  async getCityById(id: string): Promise<NiceResponse<CityResponse>> {
    const response = await this.api.client.get<NiceResponse<CityResponse>>(
      `/admin/review/cities/${id}`,
    );
    return response.data;
  }

  /**
   * Получение списка городов с фильтрацией
   */
  async getCities(
    params?: CityListParams,
  ): Promise<NiceResponse<CityListResponse>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `/admin/review/cities?${queryString}`
      : '/admin/review/cities';

    const response = await this.api.client.get<NiceResponse<CityListResponse>>(
      url,
    );
    return response.data;
  }

  /**
   * Обновление города
   */
  async updateCity(
    id: string,
    req: UpdateCityRequest,
  ): Promise<NiceResponse<CityResponse>> {
    const response = await this.api.client.put<NiceResponse<CityResponse>>(
      `/admin/review/cities/${id}`,
      req,
    );
    return response.data;
  }

  /**
   * Удаление города
   */
  async deleteCity(id: string): Promise<NiceResponse<LocationActionResponse>> {
    const response = await this.api.client.delete<
      NiceResponse<LocationActionResponse>
    >(`/admin/review/cities/${id}`);
    return response.data;
  }

  /**
   * Поиск городов
   */
  async searchCities(
    searchText: string,
    countryId?: string,
    limit: number = 20,
  ): Promise<NiceResponse<CityListResponse>> {
    return this.getCities({
      search: searchText,
      country_id: countryId,
      limit,
      page: 1,
    });
  }

  /**
   * Получение городов по стране
   */
  async getCitiesByCountry(
    countryId: string,
    limit: number = 100,
  ): Promise<NiceResponse<CityListResponse>> {
    return this.getCities({
      country_id: countryId,
      limit,
      page: 1,
      sort_by: 'created_at',
      sort_dir: 'asc',
    });
  }

  // === Методы для работы с курсами ===

  /**
   * Создание курса с переводами
   */
  async createCourse(
    req: CreateCourseRequest,
  ): Promise<NiceResponse<CourseResponse>> {
    const response = await this.api.client.post<NiceResponse<CourseResponse>>(
      '/admin/review/courses/full',
      req,
    );
    return response.data;
  }

  /**
   * Получение курса по ID
   */
  async getCourseById(id: string): Promise<NiceResponse<CourseResponse>> {
    const response = await this.api.client.get<NiceResponse<CourseResponse>>(
      `/admin/review/courses/${id}`,
    );
    return response.data;
  }

  /**
   * Получение списка курсов с фильтрацией
   */
  async getCourses(
    params?: CourseListParams,
  ): Promise<NiceResponse<CourseListResponse>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `/admin/review/courses?${queryString}`
      : '/admin/review/courses';

    const response = await this.api.client.get<
      NiceResponse<CourseListResponse>
    >(url);
    return response.data;
  }

  /**
   * Обновление курса
   */
  async updateCourse(
    id: string,
    req: UpdateCourseRequest,
  ): Promise<NiceResponse<CourseResponse>> {
    const response = await this.api.client.put<NiceResponse<CourseResponse>>(
      `/admin/review/courses/${id}`,
      req,
    );
    return response.data;
  }

  /**
   * Удаление курса
   */
  async deleteCourse(
    id: string,
  ): Promise<NiceResponse<CoursePersonActionResponse>> {
    const response = await this.api.client.delete<
      NiceResponse<CoursePersonActionResponse>
    >(`/admin/review/courses/${id}`);
    return response.data;
  }

  /**
   * Поиск курсов
   */
  async searchCourses(
    searchText: string,
    locale?: 'ru' | 'en',
    limit: number = 20,
  ): Promise<NiceResponse<CourseListResponse>> {
    return this.getCourses({
      search: searchText,
      locale,
      limit,
      page: 1,
    });
  }

  // === Методы для работы с персонами ===

  /**
   * Создание персоны
   */
  async createPerson(
    req: CreatePersonRequest,
  ): Promise<NiceResponse<PersonResponse>> {
    const response = await this.api.client.post<NiceResponse<PersonResponse>>(
      '/admin/review/persons/full',
      req,
    );
    return response.data;
  }

  /**
   * Получение персоны по ID
   */
  async getPersonById(id: string): Promise<NiceResponse<PersonResponse>> {
    const response = await this.api.client.get<NiceResponse<PersonResponse>>(
      `/admin/review/persons/${id}`,
    );
    return response.data;
  }

  /**
   * Получение списка персон с фильтрацией
   */
  async getPersons(
    params?: PersonListParams,
  ): Promise<NiceResponse<PersonListResponse>> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `/admin/review/persons?${queryString}`
      : '/admin/review/persons';

    const response = await this.api.client.get<
      NiceResponse<PersonListResponse>
    >(url);
    return response.data;
  }

  /**
   * Обновление персоны
   */
  async updatePerson(
    id: string,
    req: UpdatePersonRequest,
  ): Promise<NiceResponse<PersonResponse>> {
    const response = await this.api.client.put<NiceResponse<PersonResponse>>(
      `/admin/review/persons/${id}`,
      req,
    );
    return response.data;
  }

  /**
   * Удаление персоны
   */
  async deletePerson(
    id: string,
  ): Promise<NiceResponse<CoursePersonActionResponse>> {
    const response = await this.api.client.delete<
      NiceResponse<CoursePersonActionResponse>
    >(`/admin/review/persons/${id}`);
    return response.data;
  }

  /**
   * Поиск персон
   */
  async searchPersons(
    searchText: string,
    countryId?: string,
    cityId?: string,
    limit: number = 20,
  ): Promise<NiceResponse<PersonListResponse>> {
    return this.getPersons({
      search: searchText,
      country_id: countryId,
      city_id: cityId,
      limit,
      page: 1,
    });
  }

  /**
   * Получение персон по стране
   */
  async getPersonsByCountry(
    countryId: string,
    limit: number = 100,
  ): Promise<NiceResponse<PersonListResponse>> {
    return this.getPersons({
      country_id: countryId,
      limit,
      page: 1,
      sort_by: 'first_name',
      sort_dir: 'asc',
    });
  }

  /**
   * Получение персон по городу
   */
  async getPersonsByCity(
    cityId: string,
    limit: number = 100,
  ): Promise<NiceResponse<PersonListResponse>> {
    return this.getPersons({
      city_id: cityId,
      limit,
      page: 1,
      sort_by: 'first_name',
      sort_dir: 'asc',
    });
  }
}
